/**
 * Summary stats for a user-selected sub-range of a route (elevation-panel-v2's
 * drag-to-select feature).
 *
 * Computed on FULL-RESOLUTION coordinates/distances/altitudes (the same raw
 * props the panel receives), not the decimated points `buildElevationChartData`
 * produces for rendering — a selection's stats should reflect the real route,
 * not a display-only downsampling. This is a standalone module (not extracted
 * from `buildElevationChartData.ts`) specifically to avoid touching a file
 * with existing passing tests for an unrelated feature.
 */

import { elevationGainFromCoordinates, elevationLossFromCoordinates } from './elevationGain';
import { gradientPercentBetween } from './gradient';
import { HikeTimeUtility } from '../helpers/HikeTime';

export interface RangeStats {
  distanceMeters: number;
  gainMeters: number;
  lossMeters: number;
  /** Net (end elevation - start elevation) / distance * 100. Signed. */
  avgGradientPercent: number;
  estDurationSeconds: number;
}

const ZERO_STATS: RangeStats = {
  distanceMeters: 0,
  gainMeters: 0,
  lossMeters: 0,
  avgGradientPercent: 0,
  estDurationSeconds: 0,
};

/**
 * Index of the sample whose cumulative distance is closest to `meters`.
 * Assumes `distancesMeters` is sorted ascending (true for both planned-route
 * and recording data, which are always emitted in travel order).
 *
 * Exported for reuse by rangeHighlight.ts, which needs the same
 * meters -> full-resolution-index lookup to slice `[lng,lat]` coordinates
 * for the map highlight of a selected range.
 */
export function nearestDistanceIndex(distancesMeters: number[], meters: number): number {
  let lo = 0;
  let hi = distancesMeters.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (distancesMeters[mid] < meters) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(distancesMeters[lo - 1] - meters) <= Math.abs(distancesMeters[lo] - meters)) {
    return lo - 1;
  }
  return lo;
}

function elevationAt(
  coordinates: number[][],
  altitudes: number[] | undefined,
  index: number
): number {
  const alt = altitudes?.[index];
  if (Number.isFinite(alt)) return alt as number;
  return coordinates[index]?.[2] ?? 0;
}

/**
 * Compute distance/gain/loss/avg-gradient/estimated-duration for the sub-range
 * of the route between `startMeters` and `endMeters` (order-independent —
 * either may be the larger). Returns all-zero stats for empty input or a
 * zero-width selection, never NaN/Infinity.
 */
export function computeRangeStats(
  coordinates: number[][],
  distancesMeters: number[],
  altitudes: number[] | undefined,
  startMeters: number,
  endMeters: number,
  elevationThresholdMeters?: number
): RangeStats {
  if (!coordinates?.length || !distancesMeters?.length || coordinates.length !== distancesMeters.length) {
    return { ...ZERO_STATS };
  }

  const lowMeters = Math.min(startMeters, endMeters);
  const highMeters = Math.max(startMeters, endMeters);
  const startIndex = nearestDistanceIndex(distancesMeters, lowMeters);
  const endIndex = nearestDistanceIndex(distancesMeters, highMeters);

  if (endIndex <= startIndex) {
    return { ...ZERO_STATS };
  }

  const coordsForGain: number[][] = [];
  for (let i = startIndex; i <= endIndex; i++) {
    const [lon, lat] = coordinates[i];
    coordsForGain.push([lon, lat, elevationAt(coordinates, altitudes, i)]);
  }

  const distanceMeters = distancesMeters[endIndex] - distancesMeters[startIndex];
  const startElevation = coordsForGain[0][2];
  const endElevation = coordsForGain[coordsForGain.length - 1][2];
  const gainMeters = elevationGainFromCoordinates(coordsForGain, elevationThresholdMeters);
  const lossMeters = elevationLossFromCoordinates(coordsForGain, elevationThresholdMeters);

  return {
    distanceMeters,
    gainMeters,
    lossMeters,
    avgGradientPercent: gradientPercentBetween(
      distancesMeters[startIndex],
      startElevation,
      distancesMeters[endIndex],
      endElevation
    ),
    estDurationSeconds: HikeTimeUtility.preference().secondsDuration(gainMeters, distanceMeters),
  };
}

export interface SliderStep {
  /** Slider `max` — rounded up from the real route length to an exact multiple of `interval`. */
  max: number;
  /** Whole-metre step size. */
  interval: number;
}

/**
 * vue-slider-component requires `(max - min)` to be an EXACT multiple of
 * `interval` — it throws otherwise. Route lengths are real-world floats, so
 * naively dividing `maxDistanceMeters` by a target step count and using the
 * result as `interval` round-trips through floating-point division and back,
 * and virtually never lands on a whole number (e.g. `12345.678 / 200 =
 * 61.72839`, then `12345.678 / 61.72839` comes back as `199.9999999999997` —
 * not the exact `200` the maths implies).
 *
 * Instead: pick a WHOLE number of metres for the interval (~`targetSteps`
 * steps across the route), then round `max` UP to the nearest exact multiple
 * of that interval. Both values are then plain integers, so their ratio is
 * exact in IEEE-754 for any real-world route length. The few metres of
 * headroom `max` gains past the route's real end are harmless — a caller
 * clamping a dragged value against the real route (e.g. via
 * `nearestDistanceIndex`) naturally snaps back to the last real point.
 */
export function sliderStepFor(maxDistanceMeters: number, targetSteps = 200): SliderStep {
  const interval = Math.max(1, Math.ceil(maxDistanceMeters / targetSteps));
  const max = Math.ceil(maxDistanceMeters / interval) * interval;
  return { max, interval };
}
